/**
 * Fox & Owl brand lockup — the ONLY place FoxCRM's identity mark is drawn.
 *
 * ============================================================================
 * REAL-LOGO SWAP POINT — read this before touching brand visuals elsewhere.
 * ============================================================================
 * Nikki's real Fox & Owl logo is NOT yet available (blocked on a Canva
 * license as of 2026-07-31). Everything below is the licensed interim
 * treatment already shipped on the marketing site
 * (/home/will/fox-and-owl/site/style.css + favicon.svg) — a Fraunces
 * wordmark monogram + a gold "spark" glyph, NOT an illustrated fox/owl mark.
 * Do not use the temp files in /home/will/fox-and-owl/assets-inbox/ — both
 * are marked do-not-publish (unlicensed).
 *
 * When Nikki's real, licensed mark arrives, replace it in exactly these
 * places (nowhere else references brand art directly):
 *   1. `BrandMark` below — swap the inline <svg> spark for an <img>/<svg>
 *      of the real mark (keep the `size`/`className` prop contract so
 *      every call site below keeps working unchanged).
 *   2. `web/public/favicon.svg` — replace with the real mark's SVG.
 *   3. `web/scripts/gen-icons.mjs` — update `buildSvg()`'s glyph to match
 *      the real mark, then re-run `node scripts/gen-icons.mjs` to
 *      regenerate `web/public/pwa-192x192.png`, `pwa-512x512.png`,
 *      `pwa-maskable-512x512.png`, and `apple-touch-icon.png`.
 *   4. `web/index.html` — only if the favicon filename changes.
 * Full list of generated/static brand image files: everything in
 * `web/public/` matching `favicon*`, `pwa-*.png`, `apple-touch-icon.png`.
 * ============================================================================
 */
import { cn } from "@/lib/utils";

/**
 * The gold "spark" glyph — reused verbatim from the marketing site's
 * favicon.svg (/home/will/fox-and-owl/site/favicon.svg). A four-point
 * compass star, licensed brand asset, safe to inline here and in
 * scripts/gen-icons.mjs.
 */
function SparkGlyph({ id }: { id: string }) {
  return (
    <svg viewBox="0 0 32 32" className="h-full w-full" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#d8b46a" />
          <stop offset="1" stopColor="#b98f3e" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="7" fill="#101a15" />
      <path
        fill={`url(#${id})`}
        d="M16 4.5 C17.7 12.2 19.8 14.3 27.5 16 C19.8 17.7 17.7 19.8 16 27.5 C14.3 19.8 12.2 17.7 4.5 16 C12.2 14.3 14.3 12.2 16 4.5 Z"
      />
    </svg>
  );
}

/** Small square badge version of the mark, for the sidebar rail / mobile header. */
export function BrandMark({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-lg ring-1 ring-white/15 shadow-md",
        className,
      )}
    >
      <SparkGlyph id="fox-owl-spark" />
    </div>
  );
}

/**
 * The text monogram — "THE FOX & THE OWL" in Fraunces with a gold italic
 * ampersand, copied from the site's `.monogram` / `.amp` CSS pattern
 * (fox-and-owl/site/style.css §5 Navigation).
 */
export function Monogram({ className, dark = false }: { className?: string; dark?: boolean }) {
  return (
    <span
      className={cn(
        "font-display font-semibold uppercase tracking-[0.14em]",
        dark ? "text-cream" : "text-ink",
        className,
      )}
    >
      The Fox{" "}
      <span
        className={cn(
          "font-semibold italic",
          // Plain gold-deep fails AA at small sizes in both contexts:
          // on night it's 3.2:1 (dark contexts use gold-bright, 8.16:1
          // instead), on dawn it's 4.29:1 — under the 4.5:1 normal-text
          // minimum — so light contexts use copper-hover (gold-deep
          // darkened ~20%), which clears 6.0:1 on dawn / 7.0:1 on white
          // regardless of size or weight.
          dark ? "text-gold-bright" : "text-copper-hover",
        )}
      >
        &amp;
      </span>{" "}
      The Owl
    </span>
  );
}

/** The quiet product name — always secondary to the Monogram above it. */
export function ProductName({ className, dark = false }: { className?: string; dark?: boolean }) {
  return (
    <span
      className={cn(
        "font-display text-[11px] font-medium uppercase tracking-[0.2em]",
        // ink-soft (not sage) on dawn: sage-on-dawn is only 3.39:1, below
        // WCAG AA's 4.5:1 for small text; ink-soft clears it at 5.92:1.
        dark ? "text-gold-bright" : "text-ink-soft",
        className,
      )}
    >
      FoxCRM
    </span>
  );
}
